// The Tower of Babel. Pure. No React, no Three.js, no GameBoard.
//
// ── Codex ──
//   The Apex of Babel (Site): "When the Tower of Babel is built, draw a
//     spell, then you may cast a spell for free."
//   The Base of Babel (Site): "You may build The Tower of Babel by
//     playing The Apex of Babel atop this."
//
// FAQ: the Apex is played onto the SAME square as a The Base of Babel the
// player already controls; the two MERGE into a single site called The
// Tower of Babel. There is no "Apex first, then Base" order. The Apex may
// only be played atop a Base already in play. We model the merge by letting
// both site cards occupy the one cell (each contributes its mana + element,
// so the Tower reads as 2 mana, Air + Earth, Unique + Exceptional from the
// engine's per-site iteration with no synthetic card).

/**
 * The same-controller site named `name` sharing `sourceCard`'s exact cell,
 * or null. Used both to gate the Apex's build trigger (is a Base under it?)
 * and to validate placement (an Apex may only land atop your Base). Pure:
 * takes the same `meshes` iterable + `cellAt` resolver the scope / condition
 * machinery uses.
 *
 * @param {string} name        catalog name of the partner site (the Base).
 * @param {object} sourceCard  the freshly-played Apex.
 * @param {Iterable<[any, { position, userData: { cardInstance } }]>} meshes
 * @param {(x: number, z: number) => { col, row } | null} cellAt
 * @returns {object|null} the partner site's cardInstance, or null.
 */
export function sameControllerSiteInCell(name, sourceCard, meshes, cellAt) {
  if (!name || !sourceCard || !meshes || !cellAt) return null;
  const sourceMesh = findMesh(meshes, sourceCard.id);
  if (!sourceMesh) return null;
  const cell = cellAt(sourceMesh.position.x, sourceMesh.position.z);
  if (!cell) return null;
  for (const [, mesh] of meshes) {
    const card = mesh?.userData?.cardInstance;
    if (!card || card.id === sourceCard.id) continue;
    if (!card.isSite) continue;
    if (!!card.rotated !== !!sourceCard.rotated) continue;
    if (card.name !== name) continue;
    const otherCell = cellAt(mesh.position.x, mesh.position.z);
    if (otherCell && otherCell.col === cell.col && otherCell.row === cell.row) return card;
  }
  return null;
}

function findMesh(meshes, id) {
  for (const [, mesh] of meshes) {
    if (mesh?.userData?.cardInstance?.id === id) return mesh;
  }
  return null;
}
